import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Pagination from "components/Pagination";
import {
  getOrders,
  createOrder,
  getShopifyOrders,
  getOrderByID,
  getEtsyOrders,
  updateOrderStatus,
  duplicateOrder,
  submitOrderToCanada,
  submitOrderToUSA,
  saveOrderData,
  orderSubmitNotification,
  wooAllOrders,
  getShopifyOrdersCount,
  getShopifyMoreOrders,
  wooMoreOrders,
  getShopifyOrder,
} from "actions/orders";
import { USD_TO_CAD_CONVERSION_RATE } from "constants/currency";
import CreateOrder from "pages/orders/CreateOrder";
import { ROUTES } from "shared/constants/routes";
import SubHeader from "components/SubHeader";
import SearchBar from "components/SearchBar";
import OrderTable from "pages/orders/table";
import OrderFilters from "pages/orders/OrderFilter";
import VoidOrderDialog from "pages/orders/VoidOrderDialog";
import RetrieveSuccessDialog from "pages/orders/RetrieveSuccessDialog";
import RetrieveLatestOrder from "pages/orders/RetrieveLatestOrder";
import Stripe from "components/Stripe";
import VendorReferenceDialog from "pages/orders/VendorReferenceDialog";
import {
  Container,
  SearchWrapper,
  PaginationWrapper,
  OrderSection,
} from "styles/pages/orders";
import DuplicateOrderModal from "pages/orders/table/DuplicateOrderModal";
import { json2xml } from "utils/jsonToXmlParser";
import OrderSubmittedModal from "pages/orders/OrderSubmittedModal";
import { getProdShipCostByDestAndPP } from "selectors/products";
import { stripeCharge, getAllCards, calculateTax } from "actions/stripe";

const XMLParser = require("react-xml-parser");
const majorProductIds = [1, 2, 446, 450, 454, 455, 457, 459, 1614798309];
const subMajorIds = [2001];

const getFulfillmentCost = (order = {}) => {
  const { OrderLines } = order;
  if (OrderLines?.length) {
    const { OrderLineItems } = OrderLines[0];
    let fulfillmentCost = 0;

    for (let i = 0; i < OrderLineItems?.length; i++) {
      fulfillmentCost += OrderLineItems[i]?.FulfillmentCost;
    }
    return Number(fulfillmentCost.toFixed(2)) || 0;
  }
};

const getTaxCost = (tax = [], order) => {
  const selectedPrintProviderTaxInfo = tax.find(
    (c) => c?.PrintProvider === order?.PrintProvider
  );

  if (selectedPrintProviderTaxInfo) {
    const selectedDestinationCountry =
      selectedPrintProviderTaxInfo?.TaxCharges?.find(
        (c) => c?.ShipppingDestinationCountry === order?.CountryString
      );
    if (selectedDestinationCountry) {
      const selectedTaxCharge = selectedDestinationCountry?.TaxCharges?.find(
        (c) => c?.ShippingState === order?.StateProvince
      );
      return selectedTaxCharge?.TaxCharge || 0;
    }
    return 0;
  }
  return 0;
};

const Orders = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [createOrderModal, setCreateOrderModal] = useState(false);
  const [voidOrder, setVoidOrder] = useState(undefined);
  const [retrievalSuccessModal, setRetrievalSuccessModal] = useState(false);
  const [orderBySearch, setOrdersBySearch] = useState(false);
  const [orders, setOrders] = useState(undefined);
  const [category, setCategory] = useState("PendingDetails");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const [cards, setCards] = useState(undefined);
  const [paymentModal, setPaymentModal] = useState(false);
  const products = useSelector((state) => state?.products?.data);
  const auth = useSelector((state) => state?.auth);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const countries = useSelector((state) => state?.countries?.data);
  const [taxInfo, setTaxInfo] = useState(undefined);
  const [ordersSubmitted, setOrdersSubmitted] = useState(false);
  const [vendorReferenceVisibility, setVendorReferenceVisibility] =
    useState(false);
  const {
    profile: { paymentSkip, emailid, group },
  } = auth;
  const [duplicateModalVisibility, setDuplicateModalVisibility] =
    useState(false);
  const prevCategory = useRef();

  useEffect(() => {
    const getOrdersData = async () => {
      let skip = currentPage;

      if (category !== prevCategory.current) {
        prevCategory.current = category;
        skip = 1;
      }

      const orders = await dispatch(
        getOrders({
          emailid,
          state: category,
          skip: (skip - 1) * 10,
        })
      );
      if (!orders.error) {
        setOrders(orders);
      }
    };
    getOrdersData();
  }, [dispatch, category, currentPage, emailid]);

  useEffect(() => {
    const fetchTaxInfo = async () => {
      if (!cards) {
        const tax = await dispatch(calculateTax());

        if (tax.error) {
          return;
        }

        setTaxInfo(tax);
      }
    };
    fetchTaxInfo();
  }, [dispatch, emailid]);

  const payableAmount = (orders) => {
    let cost = [];

    for (let i = 0; i < orders?.length; i++) {
      const order = orders[i];
      const orderLines = order?.OrderLines[0];
      const orderLineItems = orderLines?.OrderLineItems;

      let product = orderLineItems[0];
      let quantity = 0;

      for (let i = 0; i < orderLineItems.length; i++) {
        if (_.includes(subMajorIds, orderLineItems[i].ProductID)) {
          product = orderLineItems[i];
          break;
        }

        if (_.includes(majorProductIds, orderLineItems[i].ProductID)) {
          product = orderLineItems[i];
          break;
        }
      }

      for (let i = 0; i < orderLineItems.length; i++) {
        quantity += Number(orderLineItems[i]?.OliQuantity) || 0;
      }

      const shippingCost = getProdShipCostByDestAndPP(
        products,
        order?.PrintProvider,
        order?.CountryString,
        product,
        quantity,
        countries
      );
      cost.push(
        Number(
          (
            shippingCost?.cost +
            getFulfillmentCost(order) +
            Number(
              (
                ((shippingCost?.cost + getFulfillmentCost(order)) *
                  getTaxCost(taxInfo, order)) /
                100
              ).toFixed(2)
            )
          ).toFixed(2)
        )
      );
    }
    return cost;
  };

  return (
    <Container>
      <SubHeader
        title="Orders"
        links={[
          {
            title: ROUTES.DASHBOARD.TITLE,
            to: ROUTES.DASHBOARD.LINK,
          },
        ]}
        currentPageTitle="Orders"
      />
      <SearchWrapper>
        <OrderSection>
          <CreateOrder
            open={createOrderModal}
            show={() => setCreateOrderModal(true)}
            hide={() => setCreateOrderModal(false)}
            handleNext={() =>
              setActiveStep((prevActiveStep) => prevActiveStep + 1)
            }
            handleBack={() =>
              setActiveStep((prevActiveStep) => prevActiveStep - 1)
            }
            activeStep={activeStep}
            save={async (payload) => {
              setCreateOrderModal(false);
              const order = await dispatch(
                createOrder({
                  newOrder: payload,
                  user: emailid,
                })
              );

              if (order.error) {
                return;
              }

              history.push(`${ROUTES.ORDER_DETAILS.LINK}/${order.ID}`);
            }}
          />
        </OrderSection>
        <SearchBar
          fetchAllOrders={async () => {
            if (orderBySearch) {
              const orders = await dispatch(
                getOrders({
                  emailid,
                  state: "PendingDetails",
                  skip: 0,
                })
              );
              if (!orders.error) {
                setOrders(orders);
              }
              setCategory("PendingDetails");
              setOrdersBySearch(false);
            }
          }}
          searchById={async (orderId) => {
            if (orderId) {
              const orders = await dispatch(
                getOrderByID({
                  user: emailid,
                  id: orderId,
                })
              );

              if (orders?.error) {
                return;
              }

              setOrders(orders);
              setOrdersBySearch(true);
            }
          }}
        />

        <RetrieveLatestOrder
          retrieveShopifyOrders={async (store) => {
            if (store?.MarketplaceType === "Etsy") {
              await dispatch(getEtsyOrders(emailid, store?.ShopID, store?.ID));
            } else if (store?.MarketplaceType === "WooCommerce") {
              let response = await dispatch(
                wooAllOrders(emailid, store?.ShopID)
              );

              if (response?.next_page_info) {
                let condition = true;

                while (condition) {
                  await new Promise(async (resolve) => {
                    response = await dispatch(
                      wooMoreOrders(
                        emailid,
                        store?.ShopID,
                        response?.next_page_info
                      )
                    );

                    if (!response?.next_page_info) {
                      condition = false;
                    }

                    resolve();
                  });
                }
              }
            } else {
              let response;

              const count = await dispatch(
                getShopifyOrdersCount(emailid, store?.ShopID)
              );

              if (count?.count <= 100) {
                response = await dispatch(
                  getShopifyOrders(emailid, store?.ShopID)
                );
              } else {
                const loop = Math.ceil(count?.count / 100);
                const arr = _.range(loop);

                for await (const index of arr) {
                  await new Promise(async (resolve, reject) => {
                    if (index === 0) {
                      response = await dispatch(
                        getShopifyOrders(emailid, store?.ShopID)
                      );
                    } else if (index !== 0) {
                      response = await dispatch(
                        getShopifyMoreOrders(
                          emailid,
                          store?.ShopID,
                          response?.next_page_info
                        )
                      );
                    }

                    resolve();
                  });
                }
              }
            }
            if (category === "PendingDetails" && currentPage === 1) {
              const orders = await dispatch(
                getOrders({
                  emailid,
                  state: category,
                  skip: (currentPage - 1) * 10,
                })
              );
              if (!orders.error) {
                setOrders(orders);
              }
            } else {
              setCategory("PendingDetails");
              setCurrentPage(1);
            }
            setRetrievalSuccessModal(true);
          }}
          loggedInUser={true}
        />
      </SearchWrapper>
      <OrderFilters
        updateCategory={(v) => {
          setCategory(v);
          setCurrentPage(1);
        }}
        paymentSkip={paymentSkip}
        selected={category}
        orderCounts={orders?.orderCounts}
        rowsSelected={!!orders?.orders?.filter((p) => p?.checked)?.length}
        payButtonClick={async () => {
          let selectedOrders = orders?.orders?.filter((p) => p?.checked);
          selectedOrders = selectedOrders?.map((p) => ({
            ...p,
            checked: false,
          }));

          if (paymentSkip === true) {
            for await (let order of selectedOrders) {
              await new Promise(async (resolve, reject) => {
                const xml = json2xml(order, products, emailid, auth);

                const shippingCost = 0;

                const grandTotalInUsd = Number(
                  (((shippingCost + getFulfillmentCost(order)) *
                    USD_TO_CAD_CONVERSION_RATE *
                    getTaxCost(taxInfo, order)) /
                    100 +
                    (shippingCost + getFulfillmentCost(order)) *
                      USD_TO_CAD_CONVERSION_RATE) /
                    USD_TO_CAD_CONVERSION_RATE
                ).toFixed(2);

                const taxInCad = (
                  ((shippingCost + getFulfillmentCost(order)) *
                    USD_TO_CAD_CONVERSION_RATE *
                    getTaxCost(taxInfo, order)) /
                  100
                ).toFixed(2);

                const submitOrderResponse =
                  order?.PrintProvider === "CA"
                    ? await dispatch(submitOrderToCanada(xml, token))
                    : await dispatch(submitOrderToUSA(xml, token));

                const parseOrderResponse = new XMLParser().parseFromString(
                  submitOrderResponse
                );

                const errorCode =
                  parseOrderResponse.getElementsByTagName("ErrorCode");

                if (errorCode?.length) {
                  if (errorCode[0].value != 0) {
                    alert(
                      "Error with the order, please contact BOT for more details at developer@bigoventees.com"
                    );
                    return;
                  }
                }

                const orderStatus = await dispatch(
                  await updateOrderStatus({
                    user: emailid,
                    orderId: order?.ID,
                    status: "InProduction",
                    productionDate: moment().format("YYYY-MM-DD"),
                  })
                );

                await dispatch(
                  saveOrderData({
                    orderData: {
                      OrderSubmitToProductionDate:
                        moment().format("YYYY-MM-DD"),
                      ShirtlyOrderData: {
                        ...orderStatus,
                        grandTotalInUSD: grandTotalInUsd,
                        grandTotalInCAD: Number(
                          grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE
                        ).toFixed(2),
                      },
                      Group: group,
                      StripeChargeInfo: null,
                      PaypalChargeInfo: null,
                    },
                  })
                );

                await dispatch(
                  orderSubmitNotification({
                    orderData: order,
                    grandTotalInUsd: Number(grandTotalInUsd),
                    grandTotalInCan:
                      Number(grandTotalInUsd) * USD_TO_CAD_CONVERSION_RATE,
                    taxInCad: Number(taxInCad),
                  })
                );

                resolve();
              });
            }

            if (currentPage === 1) {
              const orders = await dispatch(
                getOrders({
                  emailid,
                  state: category,
                  skip: 0,
                })
              );
              if (!orders.error) {
                setOrders(orders);
              }
            } else {
              setCurrentPage(1);
            }

            setOrdersSubmitted(true);
          } else {
            const cards = await dispatch(
              getAllCards({
                user: emailid,
              })
            );
            if (cards.error) {
              return;
            }
            setCards(cards);
            setPaymentModal(selectedOrders);
          }
        }}
        handleVendorsReferenceClick={() => setVendorReferenceVisibility(true)}
      />

      <OrderTable
        orders={orders?.orders}
        getShopifyOrder={(store, orderId) => {
          dispatch(
            getShopifyOrder({ user: emailid, shopName: store, orderId })
          );
        }}
        category={category}
        voidOrder={(order) => {
          setVoidOrder(order);
        }}
        copyOrder={(order) => {
          setDuplicateModalVisibility(order);
        }}
        toggleOrderForPayment={(index, checked) => {
          setOrders({
            ...orders,
            orders: orders?.orders?.map((order, j) => {
              if (index === -1) {
                return {
                  ...order,
                  checked,
                };
              }

              if (index !== j) {
                return order;
              }

              return {
                ...order,
                checked,
              };
            }),
          });
        }}
      />
      <PaginationWrapper>
        <Pagination
          totalCount={
            category === "PendingDetails"
              ? orders?.orderCounts?.PendingDetails
              : category === "AwaitingPayment"
              ? orders?.orderCounts?.AwaitingPayment
              : category === "InProduction"
              ? orders?.orderCounts?.InProduction
              : orders?.orderCounts?.Shipped
          }
          take={10}
          page={currentPage}
          onChange={(e, v) => setCurrentPage(v)}
        />
      </PaginationWrapper>
      <RetrieveSuccessDialog
        open={retrievalSuccessModal}
        setModal={() => setRetrievalSuccessModal(false)}
      />
      <VoidOrderDialog
        open={!!voidOrder}
        setModal={() => setVoidOrder(undefined)}
        onAgree={async () => {
          await dispatch(
            updateOrderStatus({
              user: emailid,
              orderId: voidOrder?.ID,
              status: "Void",
            })
          );
          setVoidOrder(undefined);
          if (currentPage === 1) {
            const orders = await dispatch(
              getOrders({
                emailid,
                state: category,
                skip: 0,
              })
            );
            if (!orders.error) {
              setOrders(orders);
            }
          } else {
            setCurrentPage(1);
          }
        }}
      />

      {paymentModal && (
        <Stripe
          multiple={true}
          payableAmount={payableAmount(paymentModal)}
          open={!!paymentModal}
          cards={cards}
          hide={async (data) => {
            setPaymentModal(false);

            for await (let order of paymentModal) {
              await new Promise(async (resolve, reject) => {
                if (data) {
                  let charge;
                  // if (data.paypal) {
                  //   const payload = { orderId: data.orderId };
                  //   charge = await dispatch(paypalCharge(payload));
                  //   if (charge.error) {
                  //     alert(charge.message);
                  //     return;
                  //   }
                  // } else {
                  const orderLines = order?.OrderLines[0];
                  const orderLineItems = order?.OrderLines[0]?.OrderLineItems;

                  let product = orderLineItems[0];
                  let quantity = 0;

                  for (let i = 0; i < orderLineItems.length; i++) {
                    if (_.includes(subMajorIds, orderLineItems[i].ProductID)) {
                      product = orderLineItems[i];
                      break;
                    }

                    if (
                      _.includes(majorProductIds, orderLineItems[i].ProductID)
                    ) {
                      product = orderLineItems[i];
                      break;
                    }
                  }

                  for (let i = 0; i < orderLineItems.length; i++) {
                    quantity += Number(orderLineItems[i]?.OliQuantity) || 0;
                  }

                  const shippingCost = getProdShipCostByDestAndPP(
                    products,
                    order?.PrintProvider,
                    order?.CountryString,
                    product,
                    quantity,
                    countries
                  );

                  const grandTotalInUsd = Number(
                    (((shippingCost?.cost + getFulfillmentCost(order)) *
                      USD_TO_CAD_CONVERSION_RATE *
                      getTaxCost(taxInfo, order)) /
                      100 +
                      (shippingCost?.cost + getFulfillmentCost(order)) *
                        USD_TO_CAD_CONVERSION_RATE) /
                      USD_TO_CAD_CONVERSION_RATE
                  ).toFixed(2);

                  const taxInCad = (
                    ((shippingCost?.cost + getFulfillmentCost(order)) *
                      USD_TO_CAD_CONVERSION_RATE *
                      getTaxCost(taxInfo, order)) /
                    100
                  ).toFixed(2);

                  const payload = {
                    user: emailid,
                    saveCard: data?.saveCard,
                    cardDetails: data?.cardDetails,
                    // amount: 100,
                    ID: order?.ID,
                    currency: data?.paymentInCanadian ? "CAD" : "USD",
                    ReceiptID: order?.ReceiptID,
                    amount: data.paymentInCanadian
                      ? parseInt(
                          (
                            (shippingCost?.cost +
                              getFulfillmentCost(order) +
                              Number(
                                (
                                  ((shippingCost?.cost +
                                    getFulfillmentCost(order)) *
                                    getTaxCost(taxInfo, order)) /
                                  100
                                ).toFixed(2)
                              )) *
                            USD_TO_CAD_CONVERSION_RATE
                          ).toFixed(2) * 100
                        )
                      : parseInt(
                          (shippingCost?.cost +
                            getFulfillmentCost(order) +
                            Number(
                              (
                                ((shippingCost?.cost +
                                  getFulfillmentCost(order)) *
                                  getTaxCost(taxInfo, order)) /
                                100
                              ).toFixed(2)
                            )) *
                            100
                        ),
                  };

                  charge = await dispatch(stripeCharge(payload, token));
                  if (charge.error) {
                    return;
                  }
                  if (
                    !charge ||
                    !charge?.status ||
                    charge?.status !== "succeeded" ||
                    charge?.statusCode === 402 ||
                    charge?.statusCode === 400 ||
                    charge?.type === "StripeCardError" ||
                    charge?.type === "StripeInvalidRequestError" ||
                    charge?.rawType === "card_error" ||
                    charge?.rawType === "invalid_request_error" ||
                    charge?.code === "card_declined"
                  ) {
                    alert("Payment declined");
                    return;
                  }
                  // }
                  const xml = json2xml(order, products, emailid, auth);
                  const submitOrderResponse =
                    order?.PrintProvider === "CA"
                      ? await dispatch(submitOrderToCanada(xml, token))
                      : await dispatch(submitOrderToUSA(xml, token));
                  const parseOrderResponse = new XMLParser().parseFromString(
                    submitOrderResponse
                  );
                  const errorCode =
                    parseOrderResponse.getElementsByTagName("ErrorCode");
                  if (errorCode?.length) {
                    if (errorCode[0].value != 0) {
                      alert(
                        "Error with the order, please contact BOT for more details at developer@bigoventees.com"
                      );
                      return;
                    }
                  }
                  const orderStatus = await dispatch(
                    await updateOrderStatus({
                      user: emailid,
                      orderId: order?.ID,
                      status: "InProduction",
                      productionDate: moment().format("YYYY-MM-DD"),
                    })
                  );
                  await dispatch(
                    saveOrderData({
                      orderData: {
                        OrderSubmitToProductionDate:
                          moment().format("YYYY-MM-DD"),
                        ShirtlyOrderData: {
                          ...orderStatus,
                          grandTotalInUSD: grandTotalInUsd,
                          grandTotalInCAD: Number(
                            grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE
                          ).toFixed(2),
                        },
                        StripeChargeInfo: data?.paypal ? null : charge,
                        PaypalChargeInfo: data?.paypal ? charge : null,
                        Group: group,
                        // DataForMachine: xml,
                      },
                    })
                  );
                  await dispatch(
                    orderSubmitNotification({
                      orderData: order,
                      grandTotalInUsd: Number(grandTotalInUsd),
                      grandTotalInCan:
                        Number(grandTotalInUsd) * USD_TO_CAD_CONVERSION_RATE,
                      taxInCad: Number(taxInCad),
                    })
                  );
                  setPaymentModal(false);
                } else {
                  setPaymentModal(false);
                }
                resolve();
              });
            }

            if (data) {
              if (currentPage === 1) {
                const orders = await dispatch(
                  getOrders({
                    emailid,
                    state: category,
                    skip: 0,
                  })
                );
                if (!orders.error) {
                  setOrders(orders);
                }
              } else {
                setCurrentPage(1);
              }

              setOrdersSubmitted(true);
            }
          }}
        />
      )}
      {duplicateModalVisibility && (
        <DuplicateOrderModal
          open={!!duplicateModalVisibility}
          setModal={async (v) => {
            const order = duplicateModalVisibility;
            setDuplicateModalVisibility(false);
            if (v?.value) {
              let orders = [];
              for (let i = 0; i < v?.value; i++) {
                const currentDate = moment().format("YYYY-MM-DD");
                const orderNumber = order?.ID + _.random(1, 999999999) + i;
                const orderLinesId =
                  order?.OrderLines[0]?.ID + _.random(1, 999999999) + i;
                const payload = {
                  ...order,
                  ID: orderNumber,
                  ReceiptID: `BOT-${orderNumber}`,
                  DateCreated: currentDate,
                  OrderLines: order?.OrderLines?.map((orderLine) => {
                    return {
                      ...orderLine,
                      ID: orderLinesId,
                      OrderID: orderNumber,
                      CreateDate: currentDate,
                      OrderLineItems: orderLine?.OrderLineItems?.map(
                        (orderLineItem) => {
                          return {
                            ...orderLineItem,
                            ID: orderLineItem?.ID + _.random(1, 999999999) + i,
                            OrderLineID: orderLinesId,
                            CreateDate: currentDate,
                          };
                        }
                      ),
                    };
                  }),
                };
                delete payload?._id;
                delete payload?.checked;
                orders.push(payload);
              }

              await dispatch(
                duplicateOrder({
                  orders,
                  user: emailid,
                })
              );

              if (currentPage === 1) {
                const orders = await dispatch(
                  getOrders({
                    emailid,
                    state: category,
                    skip: 0,
                  })
                );
                if (!orders.error) {
                  setOrders(orders);
                }
              } else {
                setCurrentPage(1);
              }
            }
          }}
        />
      )}
      {ordersSubmitted && (
        <OrderSubmittedModal
          open={ordersSubmitted}
          setModal={() => setOrdersSubmitted(false)}
        />
      )}

      {vendorReferenceVisibility && (
        <VendorReferenceDialog
          open={vendorReferenceVisibility}
          hideModal={() => setVendorReferenceVisibility(false)}
          selectedOrders={orders?.orders?.filter((p) => p?.checked)}
          reload={async () => {
            setVendorReferenceVisibility(false);
            if (currentPage === 1) {
              const orders = await dispatch(
                getOrders({
                  emailid,
                  state: category,
                  skip: 0,
                })
              );
              if (!orders.error) {
                setOrders(orders);
              }
            } else {
              setCurrentPage(1);
            }
          }}
        />
      )}
    </Container>
  );
};

export default Orders;
